import mixpanel from "mixpanel-browser";
import { Fragment, useEffect, useState } from "react";
import { BiUserCircle } from "react-icons/bi";
import { FaChartBar, FaMapMarkedAlt } from "react-icons/fa";
import { FaCarRear } from "react-icons/fa6";
import { HiTicket } from "react-icons/hi2";
import { RxDashboard, RxFileText } from "react-icons/rx"; // Ensure this import is correct
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import Search from "@Components/Search";
import UserMenu from "@Components/UserMenu";
import useCurrentUser from "@Hooks/useCurrentUser";
import { capitalize } from "@Utils/functions";

import { User } from "@Types/users";
import { hasActionPermissions } from "@Utils/permissions";
import { AiOutlineTeam } from "react-icons/ai";
import { IoPersonOutline } from "react-icons/io5";
import { MdOutlineSecurity } from "react-icons/md";
import ColoredLogo from "../ColoredLogo";
import styles from "./Layout.module.scss";
import HelpCenterButton from "@Components/HelpCenterButton";
import { TbFileUnknown } from "react-icons/tb";

const getPageFromPath = (path: string) => {
  const pathAsArray = path.split("/").filter((x) => x);
  if (pathAsArray.length === 2) {
    if (pathAsArray[1] === "new") {
      return {
        event: `Visited new ${capitalize(pathAsArray[0])} page`,
        id: undefined,
      };
    } else {
      return {
        event: `Visited individual ${capitalize(pathAsArray[0])} page`,
        id: pathAsArray[1],
      };
    }
  }
  if (pathAsArray.length === 1) {
    return {
      event: `Visited page ${capitalize(pathAsArray[0])}`,
      id: undefined,
    };
  }
  return { event: "Visited unknown page", id: undefined };
};
const menuLinks = [
  {
    text: "Dashboard",
    id: "dashboard",
    icon: RxDashboard ? <RxDashboard /> : null,
    path: "/dashboard",
  },
  {
    text: "Loans",
    id: "loans",
    icon: <RxFileText />,
    path: "/loans",
    show: (user?: User) => {
      return hasActionPermissions("deal", "read", user);
    },
  },
  {
    text: "Dealers",
    id: "dealers",
    icon: <FaCarRear />,
    path: "/dealers",
    show: (user?: User) => {
      return hasActionPermissions("dealership", "read", user);
    },
  },
  {
    text: "Tickets",
    id: "tickets",
    icon: <HiTicket />,
    path: "/tickets",
    show: (user?: User) => {
      return hasActionPermissions("lender_ticket", "read", user);
    },
  },
  {
    text: "Users",
    id: "users",
    icon: <BiUserCircle />,
    path: "/users",
    show: (user?: User) => {
      return hasActionPermissions("lender_user", "read", user);
    },
  },
  {
    text: "Title Info",
    id: "titleIssues",
    icon: <TbFileUnknown />,
    show: (user?: User) => {
      return hasActionPermissions("title_info", "read", user);
    },
    path: "/title_info",
  },
  {
    text: "Reports",
    id: "reports",
    icon: <FaChartBar />,
    path: undefined,
    show: (user?: User) => {
      return (
        hasActionPermissions("dealership", "read", user) &&
        hasActionPermissions("deal", "read", user)
      );
    },
    entities: [
      {
        text: "Interactive map",
        id: "applications_map",
        icon: <FaMapMarkedAlt />,
        path: "/dealers/map",
        show: (user?: User) => {
          return (
            hasActionPermissions("dealership", "read", user) &&
            hasActionPermissions("deal", "read", user)
          );
        },
      },
    ],
  },
  {
    text: "Permissions",
    id: "permissions",
    icon: <MdOutlineSecurity />,
    path: undefined,
    show: (user?: User) => {
      return hasActionPermissions("lender_role", "read", user);
    },
    entities: [
      {
        text: "Roles",
        id: "roles",
        icon: <IoPersonOutline />,
        path: "/roles",
        show: (user?: User) => {
          return hasActionPermissions("lender_role", "read", user);
        },
      },
      {
        text: "Teams",
        id: "teams",
        icon: <AiOutlineTeam />,
        path: "/teams",
        show: (user?: User) => {
          return hasActionPermissions("lender_team", "read", user);
        },
      },
    ],
  },
];
const Layout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const [nestedItemActive, setNestedItemActive] = useState<number | undefined>(
    undefined,
  );
  useEffect(() => {
    const { event, id } = getPageFromPath(pathname);
    mixpanel.track(event, {
      email: user?.data?.info?.email,
      lenderName: user?.data?.lender?.data?.info?.name,
      ...(id ? { entityId: id } : {}),
    });
  }, [pathname]);
  return (
    <div
      style={{
        display: "flex",
        backgroundColor: "#fafbff",
      }}
    >
      <div className={styles.sidebar}>
        <div
          className={styles.logoContainer}
          onClick={() => navigate("/dashboard")}
        >
          <div className={styles.logo}>
            <ColoredLogo />
          </div>

          <hr color={"#6b97f6"} />
          <div className={styles.logoText}>
            {user?.data.lender?.data?.info?.name}
          </div>
        </div>
        <div className={styles.menu}>
          {menuLinks.map(({ icon, text, path, entities, id, show }, index) => {
            if (Array.isArray(entities)) {
              if (!show || show(user)) {
                return (
                  <Fragment key={index}>
                    <div
                      id={`nav-menu-${id || "link" + index}`}
                      onClick={() =>
                        setNestedItemActive((prev) => {
                          if (typeof prev === "number" && prev !== index) {
                            return undefined;
                          }
                          return index;
                        })
                      }
                      className={[
                        styles.menuItem,
                        nestedItemActive === index
                          ? styles.activeMenuItemWithEntities
                          : "",
                      ].join(" ")}
                    >
                      <div className={styles.nonActiveIndicator} />
                      <div
                        className={[
                          styles.menuItemInfo,
                          nestedItemActive === index
                            ? styles.activeMenuItemWithEntities
                            : "",
                        ].join(" ")}
                      >
                        {icon}
                        <span>{text}</span>
                      </div>
                    </div>
                    {nestedItemActive === index &&
                      entities.map((entity, entityIndex) => {
                        if (!entity.show || entity.show(user)) {
                          console.log(
                            pathname,
                            pathname.split("/").length,
                            entity.path,
                            entity.path.split("/").length,
                          );
                          return (
                            <div
                              id={`nav-menu-${id || "link" + entityIndex}`}
                              key={entityIndex}
                              className={[
                                styles.menuItemSecondary,
                                pathname.includes(entity.path) &&
                                pathname.split("/").length ===
                                  entity.path.split("/").length
                                  ? styles.activeNestedMenuItem
                                  : "",
                              ].join(" ")}
                            >
                              {pathname.includes(entity.path) &&
                              pathname.split("/").length ===
                                entity.path.split("/").length ? (
                                <div className={styles.activeIndicator} />
                              ) : (
                                <div className={styles.nonActiveIndicator} />
                              )}
                              <Link to={entity.path}>
                                {entity.icon}
                                <span>{entity.text}</span>
                              </Link>
                            </div>
                          );
                        }
                        return null;
                      })}
                  </Fragment>
                );
              }
            } else if (path) {
              if (!show || show(user)) {
                return (
                  <div
                    id={`nav-menu-${id || "link" + index}`}
                    key={index}
                    onClick={() => setNestedItemActive(undefined)}
                    className={[
                      styles.menuItem,
                      pathname.includes(path) &&
                      pathname.split("/").length === path.split("/").length
                        ? styles.activeMenuItem
                        : "",
                    ].join(" ")}
                  >
                    {pathname.includes(path) &&
                    pathname.split("/").length === path.split("/").length ? (
                      <div className={styles.activeIndicator} />
                    ) : (
                      <div className={styles.nonActiveIndicator} />
                    )}
                    <Link to={path}>
                      {icon}
                      <span>{text}</span>
                    </Link>
                  </div>
                );
              }
            }
          })}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",

          flex: 1,
        }}
      >
        <div className={styles.topMenu}>
          {pathname === "/dashboard" && (
            <h1 className={styles.greeting}>
              Welcome, {user?.data.info.firstName ?? ""}
            </h1>
          )}

          <div className={styles.search}>
            <Search />
          </div>
          <HelpCenterButton />
          <UserMenu />
        </div>

        <div
          style={{
            width: "100%",
            marginBottom: "20px",
          }}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
